/**
 * PatientCredentials.tsx — FUNC-124
 *
 * Vista "Credenciales de Pacientes" del portal director — tracking del rollout
 * de SEC-064 (login paciente con PIN vía Supabase Auth). Sin props: la RPC
 * hace su propio chequeo de rol server-side, mismo patrón que
 * BitacoraSistema/DirectorOverview.
 *
 * Las 3 RPCs backend (director_listar_pacientes_credenciales,
 * director_invitar_paciente, director_actualizar_email_paciente) ya existen
 * en prod, revisadas por db+security — este componente solo las consume.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import { PatientCredentialService } from '../../services/patient-credential.service';
import './PatientCredentials.css';

// ============================================================================
// TIPOS
// ============================================================================

// 'activacion_incompleta' lo calcula la RPC de listado (SEC-064b): 'activo' sin cuenta en
// auth.users — la activación se cortó a mitad. Se reinvita igual que un 'invitado'.
type EstadoCredencial = 'sin_invitar' | 'invitado' | 'activo' | 'activacion_incompleta';

interface CredentialRow {
  patient_id: string;
  nombre: string;
  apellido: string;
  rut: string;
  email: string;
  terapeuta_id: string;
  estado_credencial: EstadoCredencial;
  invitacion_enviada_at: string | null;
  invitacion_expira: string | null;
  clave_actualizada_at: string | null;
  preseleccionado_default: boolean;
}

interface ActualizarEmailResponse {
  patient_id: string;
  email: string;
  sin_cambios: boolean;
}

type RowSendState = 'sending' | 'success' | 'error' | undefined;

// ============================================================================
// CONSTANTES
// ============================================================================

// Caso borde de la spec: si la RPC devuelve exactamente este mensaje, no es
// una falla — el paciente ya activó su cuenta antes de que llegáramos a
// invitarlo (carrera con la activación real).
const YA_ACTIVO_MENSAJE = 'El paciente ya activó su credencial';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Stagger entre invitaciones del loop secuencial del envío masivo (spec §6: 250-400ms).
const INVITE_STAGGER_MS = 320;

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// ============================================================================
// TIEMPO RELATIVO — sin dependencia nueva (el proyecto no usa date-fns/dayjs,
// ver src/lib/utils/dateUtils.ts). Devuelve solo la magnitud ("2 horas"), sin
// prefijo "hace"/"en" — cada call site arma la oración según el sentido que
// corresponda (pasado para "Enviada"/"Venció"/"Activada", futuro para "Vence").
// ============================================================================

function relativeMagnitude(iso: string): string {
  const diffMs = Math.abs(Date.now() - new Date(iso).getTime());
  const MIN = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;

  if (diffMs < MIN) return 'unos segundos';
  if (diffMs < HOUR) {
    const m = Math.max(1, Math.round(diffMs / MIN));
    return `${m} ${m === 1 ? 'minuto' : 'minutos'}`;
  }
  if (diffMs < DAY) {
    const h = Math.round(diffMs / HOUR);
    return `${h} ${h === 1 ? 'hora' : 'horas'}`;
  }
  if (diffMs < DAY * 30) {
    const d = Math.round(diffMs / DAY);
    return `${d} ${d === 1 ? 'día' : 'días'}`;
  }
  const mo = Math.round(diffMs / (DAY * 30));
  return `${mo} ${mo === 1 ? 'mes' : 'meses'}`;
}

function minutesAgoLabel(date: Date): string {
  const diffMin = Math.max(0, Math.round((Date.now() - date.getTime()) / 60_000));
  return diffMin < 1 ? 'Actualizado recién' : `Actualizado hace ${diffMin}m`;
}

function isVencida(row: CredentialRow): boolean {
  return row.estado_credencial === 'invitado' && !!row.invitacion_expira
    && new Date(row.invitacion_expira).getTime() < Date.now();
}

// ============================================================================
// BADGE DE ESTADO
// ============================================================================

type BadgeTone = 'muted' | 'orange' | 'red' | 'green';

function resolveBadge(row: CredentialRow): { label: string; tone: BadgeTone } {
  if (row.estado_credencial === 'activo') return { label: 'Activo', tone: 'green' };
  if (row.estado_credencial === 'activacion_incompleta') return { label: 'Activación incompleta', tone: 'red' };
  if (row.estado_credencial === 'invitado') {
    return isVencida(row) ? { label: 'Invitación vencida', tone: 'red' } : { label: 'Invitado', tone: 'orange' };
  }
  return { label: 'Sin invitar', tone: 'muted' };
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const PatientCredentials: React.FC = () => {
  const [rows, setRows] = useState<CredentialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [, setTick] = useState(0); // fuerza recálculo de "Actualizado hace Xm" cada 60s — sin red

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmMode, setConfirmMode] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ current: number; total: number } | null>(null);
  const [rowSendState, setRowSendState] = useState<Record<string, RowSendState>>({});
  const [rowErrorMsg, setRowErrorMsg] = useState<Record<string, string>>({});
  const [sendSummary, setSendSummary] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [flashEmailId, setFlashEmailId] = useState<string | null>(null);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const summaryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Carga ──────────────────────────────────────────────────────────────

  const loadCredenciales = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);

    // v1 sin selector de rango — usa los defaults de la RPC (spec: sept-oct 2026).
    const { data, error } = await supabase.rpc('director_listar_pacientes_credenciales', {});

    if (error) {
      console.error('[PatientCredentials] Error cargando listado:', error.code);
      setErrorMsg('No se pudo cargar el listado de credenciales. Intentá nuevamente.');
      setLoading(false);
      return;
    }

    const newRows = (data as CredentialRow[]) ?? [];
    setRows(newRows);
    setSelectedIds(new Set(
      newRows.filter(r => r.preseleccionado_default && r.estado_credencial !== 'activo').map(r => r.patient_id)
    ));
    setLastFetchedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCredenciales();
  }, [loadCredenciales]);

  // "Actualizado hace Xm": recalculado por setInterval que SOLO re-renderiza
  // texto, sin red (spec §8 — nada de websockets/polling).
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => () => {
    if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current);
  }, []);

  // ── Selección ──────────────────────────────────────────────────────────

  const eligibleRows = rows.filter(r => r.estado_credencial !== 'activo');
  const eligibleIds = eligibleRows.map(r => r.patient_id);
  const effectiveSelectedCount = eligibleIds.filter(id => selectedIds.has(id)).length;
  const allEligibleSelected = eligibleIds.length > 0 && effectiveSelectedCount === eligibleIds.length;
  const someEligibleSelected = effectiveSelectedCount > 0 && !allEligibleSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someEligibleSelected;
    }
  }, [someEligibleSelected]);

  useEffect(() => {
    if (confirmMode) confirmBtnRef.current?.focus();
  }, [confirmMode]);

  const toggleAll = () => {
    setSelectedIds(allEligibleSelected ? new Set() : new Set(eligibleIds));
  };

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Envío de invitación (unidad reutilizada por el botón de fila y por el
  // loop secuencial del envío masivo — spec §6, sin RPC bulk) ─────────────

  // La invitación la arma y la envía la Edge Function patient-invite: el token
  // de activación nunca pasa por este browser (ver SEC-073 sobre send-email).
  const sendOne = async (row: CredentialRow): Promise<'success' | 'already_active' | 'error'> => {
    const result = await PatientCredentialService.invite(row.patient_id);

    if (!result.success) {
      if (result.error === YA_ACTIVO_MENSAJE) {
        setRows(prev => prev.map(r => (r.patient_id === row.patient_id
          ? { ...r, estado_credencial: 'activo', clave_actualizada_at: r.clave_actualizada_at ?? new Date().toISOString() }
          : r)));
        return 'already_active';
      }
      setRowErrorMsg(prev => ({ ...prev, [row.patient_id]: result.error ?? 'No se pudo enviar' }));
      return 'error';
    }

    setRowErrorMsg(prev => {
      if (!(row.patient_id in prev)) return prev;
      const next = { ...prev };
      delete next[row.patient_id];
      return next;
    });
    setRows(prev => prev.map(r => (r.patient_id === row.patient_id
      ? { ...r, estado_credencial: 'invitado', invitacion_enviada_at: new Date().toISOString(), invitacion_expira: result.expira ?? null }
      : r)));
    return 'success';
  };

  const flashSuccess = (id: string) => {
    setRowSendState(prev => ({ ...prev, [id]: 'success' }));
    setTimeout(() => {
      setRowSendState(prev => {
        if (prev[id] !== 'success') return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 1500);
  };

  const handleSingleSend = async (row: CredentialRow) => {
    if (isSending || rowSendState[row.patient_id] === 'sending') return;
    setRowSendState(prev => ({ ...prev, [row.patient_id]: 'sending' }));
    const result = await sendOne(row);
    if (result === 'error') {
      setRowSendState(prev => ({ ...prev, [row.patient_id]: 'error' }));
    } else {
      flashSuccess(row.patient_id);
    }
  };

  const runBulkSend = async () => {
    const ids = eligibleIds.filter(id => selectedIds.has(id));
    setConfirmMode(false);
    setIsSending(true);
    setSendProgress({ current: 0, total: ids.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const row = rows.find(r => r.patient_id === id);
      if (row) {
        setRowSendState(prev => ({ ...prev, [id]: 'sending' }));
        const result = await sendOne(row);
        if (result === 'error') {
          failCount++;
          setRowSendState(prev => ({ ...prev, [id]: 'error' }));
        } else {
          successCount++;
          flashSuccess(id);
        }
      }
      setSendProgress({ current: i + 1, total: ids.length });
      if (i < ids.length - 1) await sleep(INVITE_STAGGER_MS);
    }

    setIsSending(false);
    setSendProgress(null);
    setSelectedIds(new Set());

    if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current);
    const summary = failCount === 0
      ? { tone: 'success' as const, text: `${successCount} ${successCount === 1 ? 'invitación enviada' : 'invitaciones enviadas'}` }
      : { tone: 'warning' as const, text: `${successCount} enviadas · ${failCount} ${failCount === 1 ? 'falló' : 'fallaron'}` };
    setSendSummary(summary);
    summaryTimeoutRef.current = setTimeout(() => setSendSummary(null), 6000);
  };

  // ── Edición inline de email ────────────────────────────────────────────

  const startEdit = (row: CredentialRow) => {
    if (isSending) return;
    setEditingId(row.patient_id);
    setEditValue(row.email);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
    setEditSaving(false);
  };

  const confirmEdit = async (id: string) => {
    const value = editValue.trim();
    if (!EMAIL_REGEX.test(value)) {
      setEditError('Formato de email inválido');
      return;
    }
    setEditSaving(true);
    setEditError(null);

    const { data, error } = await supabase.rpc('director_actualizar_email_paciente', {
      p_patient_id: id,
      p_nuevo_email: value,
    });

    if (error) {
      // Mensajes de negocio de la RPC, ya redactados para el usuario final
      // (ver spec del ticket) — se muestran tal cual, no son errores genéricos.
      setEditError(error.message);
      setEditSaving(false);
      return;
    }

    const response = data as unknown as ActualizarEmailResponse;
    setRows(prev => prev.map(r => (r.patient_id === id ? { ...r, email: response.email } : r)));
    setEditSaving(false);
    setEditingId(null);
    setFlashEmailId(id);
    setTimeout(() => setFlashEmailId(prev => (prev === id ? null : prev)), 1000);
  };

  // ── Render: celdas dependientes de estado ─────────────────────────────

  const renderDetail = (row: CredentialRow): React.ReactNode => {
    const state = rowSendState[row.patient_id];

    if (state === 'sending') {
      return <span className="cred-detail cred-detail--muted cred-detail--italic">Enviando…</span>;
    }
    if (state === 'error') {
      return (
        <span className="cred-detail cred-detail--error">
          {rowErrorMsg[row.patient_id] ?? 'No se pudo enviar'}
          <button
            type="button"
            className="cred-retry-btn"
            aria-label={`Reintentar envío a ${row.nombre} ${row.apellido}`}
            onClick={() => handleSingleSend(row)}
          >
            <i className="fas fa-rotate-right" />
          </button>
        </span>
      );
    }

    if (row.estado_credencial === 'sin_invitar') {
      return <span className="cred-detail cred-detail--muted">—</span>;
    }

    if (row.estado_credencial === 'activacion_incompleta') {
      return <span className="cred-detail cred-detail--error">La activación se cortó — reinvitá</span>;
    }

    if (row.estado_credencial === 'invitado') {
      if (isVencida(row)) {
        return (
          <span className="cred-detail cred-detail--error">
            Venció hace {row.invitacion_expira ? relativeMagnitude(row.invitacion_expira) : '—'}
          </span>
        );
      }
      return (
        <span className="cred-detail cred-detail--muted">
          {row.invitacion_enviada_at ? `Enviada hace ${relativeMagnitude(row.invitacion_enviada_at)}` : 'Enviada'}
          {row.invitacion_expira ? ` · Vence en ${relativeMagnitude(row.invitacion_expira)}` : ''}
        </span>
      );
    }

    // activo
    return (
      <span className="cred-detail cred-detail--muted">
        {row.clave_actualizada_at ? `Activada hace ${relativeMagnitude(row.clave_actualizada_at)}` : 'Activada'}
      </span>
    );
  };

  const renderAction = (row: CredentialRow): React.ReactNode => {
    if (row.estado_credencial === 'activo') {
      return <span className="cred-detail cred-detail--muted">—</span>;
    }

    const state = rowSendState[row.patient_id];

    if (state === 'sending') {
      return (
        <span className="cred-row-action-spinner" role="status" aria-label="Enviando invitación">
          <i className="fas fa-spinner fa-spin" />
        </span>
      );
    }
    if (state === 'success') {
      return (
        <span className="cred-check-flash" aria-hidden="true">
          <i className="fas fa-check" />
        </span>
      );
    }

    if (row.estado_credencial === 'sin_invitar') {
      return (
        <button
          type="button"
          className="cred-row-action cred-row-action--primary"
          disabled={isSending}
          onClick={() => handleSingleSend(row)}
        >
          <i className="fas fa-paper-plane" /> Enviar invitación
        </button>
      );
    }

    // invitado (vigente o vencido) o activación incompleta
    const incompleta = row.estado_credencial === 'activacion_incompleta';
    return (
      <button
        type="button"
        className={`cred-row-action ${incompleta || isVencida(row) ? 'cred-row-action--warn' : 'cred-row-action--muted'}`}
        disabled={isSending}
        onClick={() => handleSingleSend(row)}
      >
        <i className="fas fa-paper-plane" /> {incompleta ? 'Reinvitar' : 'Reenviar'}
      </button>
    );
  };

  // ── Render principal ───────────────────────────────────────────────────

  const allActivated = rows.length > 0 && eligibleRows.length === 0;

  return (
    <section className="cred-view">
      <header className="cred-header">
        <div>
          <h1 className="cred-title">Credenciales de Pacientes</h1>
          <p className="cred-subtitle">Rollout de login con PIN — seguimiento de activación</p>
        </div>
        <div className="cred-refresh">
          <span className="cred-refresh__label">{lastFetchedAt ? minutesAgoLabel(lastFetchedAt) : '—'}</span>
          <button
            type="button"
            className="cred-refresh__btn"
            aria-label="Actualizar listado"
            disabled={loading || isSending}
            onClick={() => loadCredenciales()}
          >
            <i className="fas fa-arrow-rotate-right" />
          </button>
        </div>
      </header>

      {errorMsg && (
        <div className="cred-error-banner" role="alert">
          <i className="fas fa-exclamation-circle" />
          <span>{errorMsg}</span>
          <button type="button" onClick={() => setErrorMsg(null)} className="cred-error-close" aria-label="Cerrar aviso">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {sendSummary && (
        <div className={`cred-summary-banner cred-summary-banner--${sendSummary.tone}`} role="status">
          <i className={`fas ${sendSummary.tone === 'success' ? 'fa-circle-check' : 'fa-triangle-exclamation'}`} />
          <span>{sendSummary.text}</span>
          <button type="button" onClick={() => setSendSummary(null)} className="cred-summary-close" aria-label="Cerrar aviso">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {!loading && !allActivated && eligibleIds.length > 0 && (
        <div className="cred-toolbar">
          {!confirmMode && !isSending && (
            <>
              <span className="cred-toolbar__count">{effectiveSelectedCount} seleccionados</span>
              <button
                type="button"
                className="cred-toolbar__send"
                disabled={effectiveSelectedCount === 0}
                onClick={() => setConfirmMode(true)}
              >
                <i className="fas fa-paper-plane" /> Enviar invitaciones ({effectiveSelectedCount})
              </button>
            </>
          )}

          {confirmMode && !isSending && (
            <div className="cred-toolbar__confirm">
              <span>¿Confirmar envío a {effectiveSelectedCount} paciente(s)?</span>
              <button type="button" className="cred-toolbar__cancel" onClick={() => setConfirmMode(false)}>
                Cancelar
              </button>
              <button type="button" ref={confirmBtnRef} className="cred-toolbar__confirm-btn" onClick={runBulkSend}>
                Sí, enviar
              </button>
            </div>
          )}

          {isSending && (
            <div className="cred-toolbar__progress">
              <span className="cred-toolbar__progress-label">
                Enviando {sendProgress?.current ?? 0}/{sendProgress?.total ?? 0}…
              </span>
              <div className="cred-progress-bar">
                <div
                  className="cred-progress-bar__fill"
                  style={{ width: `${sendProgress && sendProgress.total > 0 ? (sendProgress.current / sendProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="cred-table-wrapper">
        {loading ? (
          <div className="cred-loading">
            <div className="spinner" />
            <p>Cargando credenciales...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="cred-empty">
            <i className="fas fa-user-check" />
            <p>No hay pacientes para mostrar en este rango de actividad.</p>
          </div>
        ) : allActivated ? (
          <div className="cred-empty cred-empty--success">
            <i className="fas fa-circle-check" />
            <p>Todos los pacientes activaron su cuenta</p>
            <p className="cred-empty__hint">No queda ninguna invitación pendiente.</p>
          </div>
        ) : (
          <table className="cred-table">
            <thead>
              <tr>
                <th className="cred-th-check">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="cred-checkbox"
                    aria-label="Seleccionar todos los elegibles"
                    checked={allEligibleSelected}
                    disabled={isSending || eligibleIds.length === 0}
                    onChange={toggleAll}
                  />
                </th>
                <th>Paciente</th>
                <th>Email</th>
                <th>Estado</th>
                <th>Detalle</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const badge = resolveBadge(row);
                const isEditing = editingId === row.patient_id;
                const isActivo = row.estado_credencial === 'activo';

                return (
                  <tr key={row.patient_id} className="cred-tr" data-state={row.estado_credencial}>
                    <td className="cred-td-check">
                      <input
                        type="checkbox"
                        className="cred-checkbox"
                        aria-label={`Seleccionar a ${row.nombre} ${row.apellido}`}
                        checked={!isActivo && selectedIds.has(row.patient_id)}
                        disabled={isActivo || isSending}
                        onChange={() => toggleRow(row.patient_id)}
                      />
                    </td>
                    <td className="cred-td-patient">
                      <span className="cred-patient__name">{row.apellido}, {row.nombre}</span>
                      <span className="cred-patient__rut">{row.rut}</span>
                    </td>
                    <td className="cred-td-email">
                      {isEditing ? (
                        <div className="cred-email-edit">
                          <div className="cred-email-edit__row">
                            <input
                              type="email"
                              className="cred-email-edit__input"
                              value={editValue}
                              disabled={editSaving}
                              onChange={e => setEditValue(e.target.value)}
                              aria-label={`Nuevo email para ${row.nombre} ${row.apellido}`}
                              autoFocus
                            />
                            {editSaving ? (
                              <i className="fas fa-spinner fa-spin cred-email-edit__spinner" aria-hidden="true" />
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="cred-email-edit__confirm"
                                  aria-label="Confirmar email"
                                  onClick={() => confirmEdit(row.patient_id)}
                                >
                                  <i className="fas fa-check" />
                                </button>
                                <button
                                  type="button"
                                  className="cred-email-edit__cancel"
                                  aria-label="Cancelar edición"
                                  onClick={cancelEdit}
                                >
                                  <i className="fas fa-times" />
                                </button>
                              </>
                            )}
                          </div>
                          {editError && <span className="cred-email-edit__error">{editError}</span>}
                        </div>
                      ) : (
                        <span className={`cred-email-display ${flashEmailId === row.patient_id ? 'cred-email-display--flash' : ''}`}>
                          <span className="cred-email__text">{row.email}</span>
                          {isActivo ? (
                            <span
                              className="cred-email__locked"
                              title="Cuenta activa — para cambiar el email contactar soporte"
                              role="img"
                              aria-label="Cuenta activa — para cambiar el email contactar soporte"
                            >
                              <i className="fas fa-lock" aria-hidden="true" />
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="cred-email__edit"
                              aria-label={`Editar email de ${row.nombre} ${row.apellido}`}
                              disabled={isSending}
                              onClick={() => startEdit(row)}
                            >
                              <i className="fas fa-pen" />
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`cred-badge cred-badge--${badge.tone}`}>{badge.label}</span>
                    </td>
                    <td className="cred-td-detail">{renderDetail(row)}</td>
                    <td className="cred-td-action">{renderAction(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="cred-footnote">No se listan pacientes sin RUT registrado (no pueden invitarse).</p>
    </section>
  );
};

export default PatientCredentials;
